/**
 * ECOFORIA Theme - Global JavaScript
 * Site-wide functionality and utilities
 */

// Utility functions
const debounce = (fn, wait) => {
  let t;
  return (...args) => {
    clearTimeout(t);
    t = setTimeout(() => fn.apply(this, args), wait);
  };
};

const trapFocus = (container, elementToFocus = container) => {
  const focusableEls = container.querySelectorAll(
    'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'
  );
  const firstFocusable = focusableEls[0];
  const lastFocusable = focusableEls[focusableEls.length - 1];

  container.addEventListener('keydown', (e) => {
    if (e.key !== 'Tab') return;

    if (e.shiftKey) {
      if (document.activeElement === firstFocusable) {
        lastFocusable.focus();
        e.preventDefault();
      }
    } else {
      if (document.activeElement === lastFocusable) {
        firstFocusable.focus();
        e.preventDefault();
      }
    }
  });

  elementToFocus.focus();
};

// Global state
window.theme = window.theme || {};
window.theme.cart = {
  itemCount: parseInt(document.querySelector('[data-cart-count]')?.textContent || '0'),
};

// Cart drawer functionality
class CartDrawer {
  constructor() {
    this.drawer = document.getElementById('cart-drawer');
    if (!this.drawer) return;

    this.panel = this.drawer.querySelector('.cart-drawer__panel');
    this.content = this.drawer.querySelector('[data-cart-drawer-content]');
    this.isOpen = false;
    
    this.init();
  }

  init() {
    // Open triggers
    document.querySelectorAll('[data-cart-drawer-trigger]').forEach(trigger => {
      trigger.addEventListener('click', (e) => {
        e.preventDefault();
        this.open();
      });
    });

    // Close triggers
    document.querySelectorAll('[data-cart-drawer-close]').forEach(trigger => {
      trigger.addEventListener('click', () => this.close());
    });

    // Escape key
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && this.isOpen) {
        this.close();
      }
    });

    // Quantity buttons
    this.drawer.addEventListener('click', (e) => {
      const minus = e.target.closest('[data-quantity-minus]');
      const plus = e.target.closest('[data-quantity-plus]');
      const remove = e.target.closest('[data-remove-item]');

      if (minus) {
        const key = minus.dataset.key;
        const input = this.drawer.querySelector(`[data-quantity-input][data-key="${key}"]`);
        const newVal = Math.max(0, parseInt(input.value) - 1);
        this.updateQuantity(key, newVal);
      }

      if (plus) {
        const key = plus.dataset.key;
        const input = this.drawer.querySelector(`[data-quantity-input][data-key="${key}"]`);
        const newVal = parseInt(input.value) + 1;
        this.updateQuantity(key, newVal);
      }

      if (remove) {
        const key = remove.dataset.key;
        this.updateQuantity(key, 0);
      }
    });

    // Quantity input change
    this.drawer.addEventListener('change', (e) => {
      if (e.target.matches('[data-quantity-input]')) {
        const key = e.target.dataset.key;
        const newVal = parseInt(e.target.value);
        this.updateQuantity(key, newVal);
      }
    });
  }

  open() {
    this.drawer.setAttribute('aria-hidden', 'false');
    document.body.style.overflow = 'hidden';
    this.isOpen = true;
    trapFocus(this.panel);
  }

  close() {
    this.drawer.setAttribute('aria-hidden', 'true');
    document.body.style.overflow = '';
    this.isOpen = false;
    document.querySelector('[data-cart-drawer-trigger]')?.focus();
  }

  async updateQuantity(key, quantity) {
    try {
      const response = await fetch('/cart/change.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ id: key, quantity }),
      });

      const cart = await response.json();
      this.refresh(cart);
      this.updateCartCount(cart.item_count);
    } catch (error) {
      console.error('Error updating cart:', error);
    }
  }

  async refresh(cart) {
    try {
      const response = await fetch('/?section_id=cart-drawer');
      const html = await response.text();
      
      const parser = new DOMParser();
      const doc = parser.parseFromString(html, 'text/html');
      const newContent = doc.querySelector('[data-cart-drawer-content]');
      const newFooter = doc.querySelector('.cart-drawer__footer');
      
      if (newContent) {
        this.content.innerHTML = newContent.innerHTML;
      }
      
      const currentFooter = this.drawer.querySelector('.cart-drawer__footer');
      if (newFooter && currentFooter) {
        currentFooter.innerHTML = newFooter.innerHTML;
      } else if (newFooter && !currentFooter) {
        this.panel.appendChild(newFooter.cloneNode(true));
      } else if (!newFooter && currentFooter) {
        currentFooter.remove();
      }
    } catch (error) {
      console.error('Error refreshing cart:', error);
    }
  }

  updateCartCount(count) {
    document.querySelectorAll('[data-cart-count]').forEach(el => {
      el.textContent = count;
    });
    window.theme.cart.itemCount = count;
  }
}

// Mobile menu functionality
class MobileMenu {
  constructor() {
    this.menu = document.getElementById('mobile-menu');
    if (!this.menu) return;

    this.toggleBtn = document.querySelector('[data-mobile-menu-toggle]');
    this.closeBtn = document.querySelector('[data-mobile-menu-close]');
    this.overlay = document.querySelector('.mobile-menu__overlay');
    this.isOpen = false;

    this.init();
  }

  init() {
    this.toggleBtn?.addEventListener('click', () => this.toggle());
    this.closeBtn?.addEventListener('click', () => this.close());
    this.overlay?.addEventListener('click', () => this.close());

    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && this.isOpen) {
        this.close();
      }
    });

    // Submenu toggles
    this.menu.querySelectorAll('[data-submenu-toggle]').forEach(toggle => {
      toggle.addEventListener('click', (e) => {
        e.preventDefault();
        const parent = toggle.closest('.mobile-nav__item');
        const submenu = parent.querySelector('.mobile-nav__submenu');
        const expanded = toggle.getAttribute('aria-expanded') === 'true';

        toggle.setAttribute('aria-expanded', !expanded);
        submenu.hidden = expanded;
      });
    });
  }

  toggle() {
    this.isOpen ? this.close() : this.open();
  }

  open() {
    this.menu.setAttribute('aria-hidden', 'false');
    this.toggleBtn?.setAttribute('aria-expanded', 'true');
    document.body.style.overflow = 'hidden';
    this.isOpen = true;
    trapFocus(this.menu);
  }

  close() {
    this.menu.setAttribute('aria-hidden', 'true');
    this.toggleBtn?.setAttribute('aria-expanded', 'false');
    document.body.style.overflow = '';
    this.isOpen = false;
    this.toggleBtn?.focus();
  }
}

// Search functionality
class Search {
  constructor() {
    this.searchBar = document.getElementById('search-bar');
    this.searchInput = document.querySelector('[data-search-input]');
    this.searchResults = document.querySelector('[data-search-results]');
    this.searchTriggers = document.querySelectorAll('[data-search-trigger]');
    this.searchClose = document.querySelector('[data-search-close]');

    if (!this.searchBar || !this.searchInput) return;

    this.init();
  }

  init() {
    // Toggle search bar
    this.searchTriggers.forEach(trigger => {
      trigger.addEventListener('click', (e) => {
        e.preventDefault();
        this.open();
      });
    });

    this.searchClose?.addEventListener('click', () => this.close());

    // Search input
    this.searchInput.addEventListener('input', debounce((e) => {
      this.search(e.target.value);
    }, 300));

    // Close on escape
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && this.searchBar.classList.contains('active')) {
        this.close();
      }
    });
  }

  open() {
    this.searchBar.classList.add('active');
    this.searchInput.focus();
  }

  close() {
    this.searchBar.classList.remove('active');
    this.searchInput.value = '';
    if (this.searchResults) {
      this.searchResults.innerHTML = '';
    }
  }

  async search(query) {
    if (query.length < 3) {
      if (this.searchResults) {
        this.searchResults.innerHTML = '';
      }
      return;
    }

    try {
      const response = await fetch(`/search/suggest.json?q=${encodeURIComponent(query)}&resources[type]=product,collection,page&resources[limit]=5`);
      const data = await response.json();
      this.renderResults(data.resources.results);
    } catch (error) {
      console.error('Search error:', error);
    }
  }

  renderResults(results) {
    if (!this.searchResults) return;

    let html = '';

    if (results.products && results.products.length > 0) {
      html += '<div class="search-results__group">';
      html += '<h3 class="search-results__group-title">Products</h3>';
      results.products.forEach(product => {
        html += `
          <a href="${product.url}" class="search-results__item">
            ${product.image ? `<img src="${product.image}" alt="${product.title}" class="search-results__image">` : ''}
            <div class="search-results__info">
              <span class="search-results__title">${product.title}</span>
              <span class="search-results__price">${product.price}</span>
            </div>
          </a>
        `;
      });
      html += '</div>';
    }

    if (results.collections && results.collections.length > 0) {
      html += '<div class="search-results__group">';
      html += '<h3 class="search-results__group-title">Collections</h3>';
      results.collections.forEach(collection => {
        html += `
          <a href="${collection.url}" class="search-results__item">
            <span class="search-results__title">${collection.title}</span>
          </a>
        `;
      });
      html += '</div>';
    }

    if (results.pages && results.pages.length > 0) {
      html += '<div class="search-results__group">';
      html += '<h3 class="search-results__group-title">Pages</h3>';
      results.pages.forEach(page => {
        html += `
          <a href="${page.url}" class="search-results__item">
            <span class="search-results__title">${page.title}</span>
          </a>
        `;
      });
      html += '</div>';
    }

    if (!html) {
      html = '<p class="search-results__empty">No results found</p>';
    }

    this.searchResults.innerHTML = html;
  }
}

// Announcement bar
class AnnouncementBar {
  constructor() {
    this.bar = document.querySelector('.announcement-bar');
    this.closeBtn = document.querySelector('[data-announcement-close]');

    if (!this.bar || !this.closeBtn) return;

    this.init();
  }

  init() {
    // Check if already dismissed
    if (sessionStorage.getItem('announcement-dismissed') === 'true') {
      this.bar.remove();
      return;
    }

    this.closeBtn.addEventListener('click', () => {
      this.bar.remove();
      sessionStorage.setItem('announcement-dismissed', 'true');
    });
  }
}

// Sticky header
class StickyHeader {
  constructor() {
    this.header = document.querySelector('.header');
    if (!this.header) return;

    this.lastScrollTop = 0;
    this.headerHeight = this.header.offsetHeight;
    this.scrollThreshold = 100;

    this.init();
  }

  init() {
    window.addEventListener('scroll', () => {
      this.onScroll();
    }, { passive: true });
  }

  onScroll() {
    const scrollTop = window.pageYOffset || document.documentElement.scrollTop;

    if (scrollTop > this.scrollThreshold) {
      this.header.classList.add('header--scrolled');
    } else {
      this.header.classList.remove('header--scrolled');
    }

    // Hide/show on scroll direction
    if (scrollTop > this.lastScrollTop && scrollTop > this.headerHeight) {
      this.header.classList.add('header--hidden');
    } else {
      this.header.classList.remove('header--hidden');
    }

    this.lastScrollTop = scrollTop;
  }
}

// Form validation
class FormValidator {
  constructor(form) {
    this.form = form;
    this.init();
  }

  init() {
    this.form.addEventListener('submit', (e) => {
      if (!this.validate()) {
        e.preventDefault();
      }
    });

    this.form.querySelectorAll('input, select, textarea').forEach(field => {
      field.addEventListener('blur', () => this.validateField(field));
    });
  }

  validate() {
    let isValid = true;
    this.form.querySelectorAll('[required]').forEach(field => {
      if (!this.validateField(field)) {
        isValid = false;
      }
    });
    return isValid;
  }

  validateField(field) {
    const value = field.value.trim();
    const errorEl = field.parentNode.querySelector('.form-field__error');

    // Remove existing error
    field.classList.remove('form-field__input--error');
    if (errorEl) errorEl.remove();

    // Check required
    if (field.required && !value) {
      this.showError(field, 'This field is required');
      return false;
    }

    // Check email
    if (field.type === 'email' && value) {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(value)) {
        this.showError(field, 'Please enter a valid email address');
        return false;
      }
    }

    return true;
  }

  showError(field, message) {
    field.classList.add('form-field__input--error');
    const error = document.createElement('span');
    error.className = 'form-field__error';
    error.textContent = message;
    field.parentNode.appendChild(error);
  }
}

// Add to cart (AJAX)
async function addToCart(formData) {
  try {
    const response = await fetch('/cart/add.js', {
      method: 'POST',
      body: formData,
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.description || 'Error adding to cart');
    }

    const item = await response.json();
    
    // Update cart count
    const cartResponse = await fetch('/cart.js');
    const cart = await cartResponse.json();
    
    document.querySelectorAll('[data-cart-count]').forEach(el => {
      el.textContent = cart.item_count;
    });

    // Open cart drawer
    const cartDrawer = document.getElementById('cart-drawer');
    if (cartDrawer) {
      cartDrawer.setAttribute('aria-hidden', 'false');
      document.body.style.overflow = 'hidden';
      
      // Refresh cart drawer content
      const drawerResponse = await fetch('/?section_id=cart-drawer');
      const html = await drawerResponse.text();
      const parser = new DOMParser();
      const doc = parser.parseFromString(html, 'text/html');
      const newContent = doc.querySelector('[data-cart-drawer-content]');
      const content = cartDrawer.querySelector('[data-cart-drawer-content]');
      if (newContent && content) {
        content.innerHTML = newContent.innerHTML;
      }
    }

    return item;
  } catch (error) {
    console.error('Add to cart error:', error);
    throw error;
  }
}

// Initialize on DOM ready
document.addEventListener('DOMContentLoaded', () => {
  new CartDrawer();
  new MobileMenu();
  new Search();
  new AnnouncementBar();
  new StickyHeader();

  // Initialize form validators
  document.querySelectorAll('form[data-validate]').forEach(form => {
    new FormValidator(form);
  });

  // Product card quick add
  document.querySelectorAll('.product-card__quick-add-form').forEach(form => {
    form.addEventListener('submit', async (e) => {
      e.preventDefault();
      const btn = form.querySelector('button');
      btn.disabled = true;
      
      try {
        await addToCart(new FormData(form));
      } catch (error) {
        alert(error.message);
      } finally {
        btn.disabled = false;
      }
    });
  });

  // Lazy load images
  if ('IntersectionObserver' in window) {
    const imageObserver = new IntersectionObserver((entries, observer) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          const img = entry.target;
          if (img.dataset.src) {
            img.src = img.dataset.src;
            img.removeAttribute('data-src');
          }
          observer.unobserve(img);
        }
      });
    });

    document.querySelectorAll('img[data-src]').forEach(img => {
      imageObserver.observe(img);
    });
  }
});

// Export utilities
window.theme.addToCart = addToCart;
window.theme.debounce = debounce;
window.theme.trapFocus = trapFocus;
